// Dynamic props (§6.1, §6.2): sugar cubes and punching dummies, simulated generically as sliding
// circles. They fall off edges, hide, and come back at their home (`Entity.data`) after their
// respawn delay; a cube drops back in like a crate and head-bonks whoever stands under it.
// Server-only: in predict mode props are immovable obstacles (§18.2).
import { DT, NO_SLOT, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { Cause, EK } from '../ids';
import { MAPS, propHome } from '../maps/index';
import { approachVec, v2 } from '../math';
import type { Entity, World } from '../types';
import { nudgeBodiesMask } from './collision';

/** `Entity.data2` of a CUBE while it drops back in after a respawn. */
const CUBE_DROPPING = 1;

const slide = v2();

export function isProp(e: Entity): boolean {
  return e.kind === EK.CUBE || e.kind === EK.DUMMY;
}

/** A prop waiting out its respawn delay (`timer` counts down) is not in the arena. */
export function propHidden(e: Entity): boolean {
  return e.timer > 0;
}

export function propRadius(e: Entity): number {
  return e.kind === EK.CUBE ? TUNE.cube.radius : TUNE.dummy.radius;
}

export function propMass(e: Entity): number {
  return e.kind === EK.CUBE ? TUNE.cube.mass : TUNE.dummy.mass;
}

/** Height a crate-like drop starts from so that it lands after `ticks` under the round's gravity. */
export function dropHeight(w: World, ticks: number): number {
  const t = ticks * DT;
  return 0.5 * w.rules.gravity * t * t;
}

/** A crate-style landing (§3.1): bodies under the impact take a hazard nudge with HEAD_BONK. */
export function headBonk(w: World, x: number, y: number): void {
  const c = TUNE.crate;
  const mask = nudgeBodiesMask(w, x, y, c.headBonkRadius, c.headBonkKb, c.headBonkDizzy, Cause.CRATE, NO_SLOT, TUNE.hitDzDefault);
  for (let slot = 0; mask >> slot; slot++) if (mask & (1 << slot)) emit(w, EV.HEAD_BONK, slot);
}

/**
 * A hit on a prop along (dx, dy) (unit vector) with base knockback `kb`. Cubes take `kb / mass`
 * (§6.2); dummies take knockback like beans, scaled by their own Dizzy (`data2`), and gain Dizzy.
 */
export function knockProp(w: World, e: Entity, dx: number, dy: number, kb: number, dizzy: number): void {
  if (e.kind === EK.CUBE) {
    const dv = (kb * w.rules.kbMult) / TUNE.cube.mass;
    e.vel.x += dx * dv;
    e.vel.y += dy * dv;
    return;
  }
  const speed = kb * (1 + TUNE.dizzy.kbPerPoint * e.data2) * w.rules.kbMult;
  e.vel.x = dx * speed + TUNE.shove.keepVel * e.vel.x;
  e.vel.y = dy * speed + TUNE.shove.keepVel * e.vel.y;
  e.data2 = Math.min(TUNE.dizzy.max, Math.round(e.data2 + dizzy * w.rules.dizzyGainMult));
  emit(w, EV.DUMMY_HIT, NO_SLOT, NO_SLOT, e.id, e.data2);
}

function reappear(w: World, e: Entity): void {
  const home = propHome(w.cfg.mapId, e.data);
  e.pos.x = home.x;
  e.pos.y = home.y;
  e.vel.x = e.vel.y = 0;
  e.vz = 0;
  if (e.kind === EK.CUBE) {
    e.z = dropHeight(w, TUNE.cube.dropTicks);
    e.data2 = CUBE_DROPPING;
  } else {
    e.z = 0;
    e.data2 = 0;
  }
}

/** One tick of a cube or dummy: friction and tilt slide on the ground, gravity in the air, void and respawn. */
export function stepProp(w: World, e: Entity): void {
  if (e.timer > 0) {
    if (--e.timer === 0) reappear(w, e);
    return;
  }
  const map = MAPS[w.cfg.mapId];
  const g = map.groundAt(w, e.pos.x, e.pos.y);
  // Below the floor surface a prop has gone over an edge and never lands again.
  const sunk = e.z < -TUNE.bean.airborneZ;
  if (!sunk && g !== VOID && e.z <= g && e.vz <= 0) {
    e.z = g;
    e.vz = 0;
    approachVec(e.vel, 0, 0, (e.kind === EK.CUBE ? TUNE.cube.friction : TUNE.tumble.decel) * DT);
    if (map.slideAccel) {
      map.slideAccel(w, e.pos.x, e.pos.y, slide);
      e.vel.x += slide.x * DT;
      e.vel.y += slide.y * DT;
    }
  } else {
    e.z += e.vz * DT - 0.5 * w.rules.gravity * DT * DT;
    e.vz -= w.rules.gravity * DT;
  }
  e.pos.x += e.vel.x * DT;
  e.pos.y += e.vel.y * DT;

  const g2 = map.groundAt(w, e.pos.x, e.pos.y);
  if (!sunk && g2 !== VOID && e.z <= g2) {
    if (e.kind === EK.CUBE && e.data2 === CUBE_DROPPING) {
      e.data2 = 0;
      headBonk(w, e.pos.x, e.pos.y);
    }
    e.z = g2;
    e.vz = 0;
  }
  if (e.z <= TUNE.edge.killZ) {
    e.timer = e.kind === EK.CUBE ? TUNE.cube.respawnTicks : TUNE.dummy.respawnTicks;
    e.vel.x = e.vel.y = 0;
    e.vz = 0;
    e.z = TUNE.edge.killZ;
  }
}
